import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Delete, Hash, KeyRound, Rocket, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/components/BrandLogo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function Frame({ title, subtitle, children, footer }) {
  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-violet-100 px-4 py-10">
      <div className="hero-orb absolute -left-16 top-16 h-44 w-44 rounded-full bg-sky-200/60" />
      <div className="hero-orb hero-orb-delay absolute -right-14 bottom-8 h-52 w-52 rounded-full bg-violet-200/60" />
      <div className="relative z-10 w-full max-w-md">
        <BrandLogo className="mb-6 flex justify-center" imageClassName="h-16 w-auto" data-testid="auth-logo" />
        <div className="estudia-card animate-rise border-white/90 p-6 shadow-xl shadow-indigo-900/10 sm:p-8">
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          <div className="mt-6">{children}</div>
        </div>
        {footer && <div className="mt-5 text-center text-sm text-slate-500">{footer}</div>}
      </div>
    </main>
  );
}

function GoogleAccessButton() {
  const { loginGoogle } = useAuth();
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    let script;
    let createdScript = false;
    let onLoad;
    let onError;

    const renderButton = (clientId) => {
      if (!active || !buttonRef.current || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async ({ credential }) => {
          if (!credential) return;
          setError("");
          try {
            const principal = await loginGoogle(credential);
            navigate(principal.new_account ? "/p/estudiantes" : "/p", { replace: true });
          } catch (err) {
            const message = apiError(err, "No pudimos iniciar sesion con Google.");
            setError(message);
            toast.error(message);
          }
        },
      });
      buttonRef.current.replaceChildren();
      window.google.accounts.id.renderButton(buttonRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        shape: "pill",
        text: "continue_with",
        width: Math.min(360, buttonRef.current.clientWidth || 360),
        locale: "es",
      });
    };

    api
      .get("/auth/google/config")
      .then(({ data }) => {
        if (!active || !data.enabled || !data.client_id) return;
        setEnabled(true);
        if (window.google?.accounts?.id) {
          renderButton(data.client_id);
          return;
        }
        script = document.getElementById("google-identity-services");
        if (!script) {
          script = document.createElement("script");
          script.id = "google-identity-services";
          script.src = "https://accounts.google.com/gsi/client";
          script.async = true;
          script.defer = true;
          document.head.appendChild(script);
          createdScript = true;
        }
        onLoad = () => renderButton(data.client_id);
        onError = () => active && setError("Google no esta disponible en este momento.");
        script.onload = onLoad;
        script.onerror = onError;
      })
      .catch(() => {});

    return () => {
      active = false;
      if (script && onLoad && script.onload === onLoad) script.onload = null;
      if (script && onError && script.onerror === onError) script.onerror = null;
      if (script && createdScript) script.remove();
    };
  }, [loginGoogle, navigate]);

  return (
    <div className={enabled ? "mb-5" : "hidden"} data-testid="google-access">
      <div ref={buttonRef} className="flex min-h-10 w-full justify-center" data-testid="google-signin-button" />
      {error && <p className="mt-2 text-center text-sm text-rose-700">{error}</p>}
      <div className="my-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
        <span className="h-px flex-1 bg-slate-200" />
        o usa tu correo
        <span className="h-px flex-1 bg-slate-200" />
      </div>
    </div>
  );
}

export function LoginPage() {
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const me = await loginUser(form.email, form.password);
      navigate(me.kind === "platform_admin" ? "/admin" : "/p", { replace: true });
    } catch (err) {
      const message = apiError(err, "No pudimos iniciar sesion.");
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Frame
      title="Entrar"
      subtitle="Para padres, tutores y administradores."
      footer={
        <>
          ¿Sin cuenta?{" "}
          <Link to="/register" className="font-semibold text-blue-700" data-testid="login-to-register">
            Crear cuenta de familia
          </Link>
          <div className="mt-2">
            <Link to="/estudiante" className="font-semibold text-slate-600" data-testid="login-to-student">
              Soy estudiante
            </Link>
          </div>
        </>
      }
    >
      <GoogleAccessButton />
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">Correo</Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="email"
            className="estudia-touch mt-1.5"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="login-email-input"
          />
        </div>
        <div>
          <Label htmlFor="password">Contraseña</Label>
          <Input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            className="estudia-touch mt-1.5"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="login-password-input"
          />
        </div>
        {error && (
          <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={busy}
          className="estudia-touch w-full rounded-xl bg-blue-600 text-base font-bold hover:bg-blue-700"
          data-testid="login-submit-btn"
        >
          {busy ? "Entrando..." : "Entrar"}
        </Button>
      </form>
    </Frame>
  );
}

export function RegisterPage() {
  const { registerParent } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", family_name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await registerParent(form);
      toast.success("Cuenta creada. Crea el perfil de tu hijo para empezar.");
      navigate("/p/estudiantes", { replace: true });
    } catch (err) {
      const message = apiError(err, "No pudimos crear la cuenta.");
      setError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Frame
      title="Crear cuenta de familia"
      subtitle="Tu cuenta administra los perfiles de tus hijos."
      footer={
        <>
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" className="font-semibold text-blue-700" data-testid="register-to-login">
            Entrar
          </Link>
        </>
      }
    >
      <GoogleAccessButton />
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label htmlFor="full_name">Tu nombre</Label>
          <Input
            id="full_name"
            required
            minLength={2}
            className="estudia-touch mt-1.5"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            data-testid="register-name-input"
          />
        </div>
        <div>
          <Label htmlFor="remail">Correo</Label>
          <Input
            id="remail"
            type="email"
            required
            className="estudia-touch mt-1.5"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="register-email-input"
          />
        </div>
        <div>
          <Label htmlFor="rpassword">Contraseña (min. 8 caracteres)</Label>
          <Input
            id="rpassword"
            type="password"
            required
            minLength={8}
            className="estudia-touch mt-1.5"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="register-password-input"
          />
        </div>
        <div>
          <Label htmlFor="family_name">Nombre de la familia (opcional)</Label>
          <Input
            id="family_name"
            className="estudia-touch mt-1.5"
            value={form.family_name}
            onChange={(e) => setForm({ ...form, family_name: e.target.value })}
            data-testid="register-family-input"
          />
        </div>
        {error && (
          <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="register-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={busy}
          className="estudia-touch w-full rounded-xl bg-blue-700 text-base font-bold hover:bg-blue-800"
          data-testid="register-submit-btn"
        >
          {busy ? "Creando..." : "Crear cuenta"}
        </Button>
      </form>
    </Frame>
  );
}

export function StudentLoginPage() {
  const { loginStudent } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("estudia_student_access") || "{}");
      return { family_code: saved.family_code || "", username: saved.username || "", pin: "" };
    } catch {
      return { family_code: "", username: "", pin: "" };
    }
  });
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await loginStudent({ ...form, family_code: form.family_code.toUpperCase() });
      if (remember) {
        localStorage.setItem(
          "estudia_student_access",
          JSON.stringify({ family_code: form.family_code.toUpperCase(), username: form.username })
        );
      } else {
        localStorage.removeItem("estudia_student_access");
      }
      navigate("/s", { replace: true });
    } catch (err) {
      const message = apiError(err, "No pudimos entrar.");
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const pressDigit = (digit) => setForm((f) => ({ ...f, pin: (f.pin + digit).slice(0, 12) }));
  const completedSteps = [form.family_code.trim(), form.username.trim(), form.pin.length >= 4].filter(Boolean).length;

  return (
    <Frame
      title="¡Hola, explorador! 🚀"
      subtitle="Entra a tu espacio en tres pasos sencillos."
      footer={
        <Link to="/login" className="inline-flex items-center gap-1 font-semibold text-slate-600" data-testid="student-to-login">
          <ArrowLeft className="h-4 w-4" /> Acceso para padres y tutores
        </Link>
      }
    >
      <div className="mb-5 rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-500 p-4 text-white shadow-lg shadow-violet-200">
        <div className="flex items-center justify-between gap-3">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/20">
            <Rocket className="h-7 w-7" />
          </span>
          <div className="flex flex-1 gap-2" aria-label={`${completedSteps} de 3 pasos completos`}>
            {[1, 2, 3].map((step) => (
              <span
                key={step}
                className={`grid h-8 flex-1 place-items-center rounded-full text-sm font-extrabold ${
                  completedSteps >= step ? "bg-lime-300 text-slate-900" : "bg-white/20 text-white"
                }`}
              >
                {completedSteps >= step ? <Check className="h-4 w-4" /> : step}
              </span>
            ))}
          </div>
        </div>
        <p className="mt-3 text-sm font-semibold text-violet-50">Tu familia te dará el código, tu usuario y tu PIN.</p>
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="student-login-form">
        <div className="rounded-2xl border-2 border-violet-100 bg-violet-50/70 p-4">
          <Label htmlFor="family_code" className="flex items-center gap-2 font-bold text-violet-900">
            <span className="grid h-8 w-8 place-items-center rounded-xl bg-violet-600 text-white"><Hash className="h-4 w-4" /></span>
            1. Código de familia
          </Label>
          <Input
            id="family_code"
            required
            maxLength={12}
            autoCapitalize="characters"
            autoCorrect="off"
            placeholder="Ejemplo: A7K9P2"
            className="estudia-touch mt-3 border-violet-200 bg-white text-center font-display text-lg font-bold uppercase tracking-[0.25em]"
            value={form.family_code}
            onChange={(e) => setForm({ ...form, family_code: e.target.value.toUpperCase() })}
            data-testid="student-family-code-input"
          />
        </div>
        <div className="rounded-2xl border-2 border-cyan-100 bg-cyan-50/70 p-4">
          <Label htmlFor="username" className="flex items-center gap-2 font-bold text-cyan-900">
            <span className="grid h-8 w-8 place-items-center rounded-xl bg-cyan-500 text-white"><UserRound className="h-4 w-4" /></span>
            2. Tu usuario
          </Label>
          <Input
            id="username"
            required
            autoCapitalize="none"
            autoCorrect="off"
            placeholder="Ejemplo: lucia"
            className="estudia-touch mt-3 border-cyan-200 bg-white"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
            data-testid="student-username-input"
          />
        </div>
        <div className="rounded-2xl border-2 border-amber-100 bg-amber-50/70 p-4">
          <Label htmlFor="pin" className="flex items-center gap-2 font-bold text-amber-900">
            <span className="grid h-8 w-8 place-items-center rounded-xl bg-amber-500 text-white"><KeyRound className="h-4 w-4" /></span>
            3. Tu PIN
          </Label>
          <Input
            id="pin"
            type="password"
            required
            inputMode="numeric"
            minLength={4}
            maxLength={12}
            autoComplete="current-password"
            aria-describedby="student-pin-help"
            className="estudia-touch mt-3 border-amber-200 bg-white text-center font-display text-2xl font-bold tracking-[0.5em]"
            value={form.pin}
            onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 12) })}
            data-testid="student-pin-input"
          />
          <p id="student-pin-help" className="mt-2 text-center text-xs font-semibold text-amber-800">
            {form.pin.length ? `${form.pin.length} número${form.pin.length === 1 ? "" : "s"}` : "Pulsa los números de tu PIN"}
          </p>
          <div className="mt-3 grid grid-cols-3 gap-2" data-testid="student-pin-pad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
              <button
                key={digit}
                type="button"
                onClick={() => pressDigit(String(digit))}
                className="estudia-touch rounded-xl border-2 border-amber-100 bg-white font-display text-xl font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-100"
                data-testid={`pin-key-${digit}`}
              >
                {digit}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, pin: f.pin.slice(0, -1) }))}
              className="estudia-touch grid place-items-center rounded-xl border-2 border-rose-100 bg-white text-rose-500 shadow-sm hover:bg-rose-50"
              data-testid="pin-key-backspace"
              aria-label="Borrar último número"
            >
              <Delete className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => pressDigit("0")}
              className="estudia-touch rounded-xl border-2 border-amber-100 bg-white font-display text-xl font-bold text-slate-700 shadow-sm hover:bg-amber-100"
              data-testid="pin-key-0"
            >
              0
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, pin: "" }))}
              className="estudia-touch rounded-xl border-2 border-slate-300 bg-white text-xs font-extrabold text-slate-700 shadow-sm hover:bg-slate-50"
              data-testid="pin-key-clear"
            >
              Limpiar
            </button>
          </div>
        </div>
        <label className="flex cursor-pointer items-center gap-3 rounded-xl px-1 text-sm font-semibold text-slate-600">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="h-5 w-5 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
            data-testid="student-remember-access"
          />
          Recordar código y usuario en este dispositivo
        </label>
        {error && (
          <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="student-login-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={busy}
          className="play-button estudia-touch w-full rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-500 text-base font-extrabold hover:from-violet-500 hover:to-fuchsia-500"
          data-testid="student-login-submit-btn"
        >
          {busy ? "Abriendo tu espacio..." : "Entrar a mi aventura"}
        </Button>
      </form>
    </Frame>
  );
}
